import type { ReactNode } from "react";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { pluralizeWithCount } from "@common/helpers/string";
import { isTaskTerminatingStatus, type Task } from "@common/types/task";
import {
    EntityAvatarGroup,
    type EntityAvatarGroupEntity,
} from "@components/avatar/EntityAvatarGroup";
import { KickAvatar } from "@components/avatar/KickAvatar";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { BoardCardAvatarField } from "@design-system/Board/BoardCardAvatarField";
import { BoardTruncatedText } from "@design-system/Board/BoardTruncatedText";
import { BoardCardTag } from "@design-system/Board/BoardCardTag";
import { useBoardCardTabStop } from "@design-system/Board/boardCardFocus";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { formatDateOnly } from "@helpers/date";
import { CalendarIcon, ChatIcon, ClockIcon } from "@icons";
import { TaskBlockedIcon } from "@icons/taskStatuses";
import {
    BLOCKED_BY_FIELD,
    getBlockedByLabel,
    type TaskBlockers,
} from "../taskBlockers";
import { useTaskClientName } from "../taskClientNamesContext";
import { joinTaskCardContext, useTaskCardEntities } from "./taskCardContext";
import { getTaskChipClassName, TaskChipButton } from "./TaskChipButton";
import styles from "./TaskBoardCard.module.scss";

const OPEN_CHAT_LABEL = "Open chat";

interface TaskCardDateProps {
    /** The field, named in the tooltip and read before the date, e.g. "Created on". */
    field: string;
    icon?: ReactNode;
    /** How late the date is, in the tooltip, e.g. "3 days late". */
    detail?: string;
    /** The formatted date. */
    children: string;
}

/** A date field's tag, its field named in the tooltip as List's column is. */
export const TaskCardDate = ({
    field,
    icon = <CalendarIcon />,
    detail,
    children,
}: Readonly<TaskCardDateProps>) => (
    <BoardCardTag field={field} tooltip="fieldName" icon={icon} detail={detail}>
        {children}
    </BoardCardTag>
);

/** How late an open task's due date is, e.g. "3 days late"; none when it isn't. */
export const getDueDateLateness = ({
    dueDate,
    status,
}: Pick<Task, "status"> & { dueDate: string }) => {
    const daysLate = differenceInCalendarDays(new Date(), parseISO(dueDate));
    return !isTaskTerminatingStatus(status) && daysLate > 0
        ? `${pluralizeWithCount("day", daysLate)} late`
        : undefined;
};

/** The due date, without the year when it's this year; an open task's tooltip says how late it is. */
export const TaskCardDueDate = ({
    task,
}: {
    task: Pick<Task, "status"> & { dueDate: string };
}) => (
    <TaskCardDate
        field="Due date"
        icon={<ClockIcon />}
        detail={getDueDateLateness(task)}
    >
        {formatDateOnly(task.dueDate)}
    </TaskCardDate>
);

/**
 * The context line's look: the entities as List's stack, then
 * `Client · Entity`, the entity's name, or a count for several.
 */
export const TaskCardContextLine = ({
    entities,
    clientName,
}: {
    entities: readonly EntityAvatarGroupEntity[];
    clientName: string | undefined;
}) => (
    <EntityAvatarGroup
        entities={entities}
        max={3}
        size="xs"
        label={
            <BoardTruncatedText>
                {joinTaskCardContext({ clientName, entities })}
            </BoardTruncatedText>
        }
        className={styles.entities}
    />
);

/**
 * The card's context line for the task's entities, with the client's name
 * when given `clientWorkspaceId`.
 */
export const TaskCardEntities = ({
    entityIds,
    clientWorkspaceId,
}: {
    entityIds: number[];
    clientWorkspaceId?: string;
}) => {
    const clientName = useTaskClientName(clientWorkspaceId);
    const taskEntities = useTaskCardEntities(entityIds);
    return (
        <TaskCardContextLine entities={taskEntities} clientName={clientName} />
    );
};

/** The client the task belongs to, on the organization page. */
export const TaskCardClient = ({ workspaceId }: { workspaceId: string }) => {
    const clientName = useTaskClientName(workspaceId);
    return (
        <BoardTruncatedText className={styles.client}>
            {clientName ?? "-"}
        </BoardTruncatedText>
    );
};

type TaskPerson = NonNullable<Task["assignee"]>;

/**
 * A person field as their colourless 20px avatar alone, named with the field
 * in its tooltip (`Created by · Matt Peterson`). No person means Kick did it.
 */
export const TaskCardPerson = ({
    field,
    person,
}: {
    field: string;
    person: Pick<TaskPerson, "name" | "email" | "avatar" | "iconColor"> | null;
}) => (
    <BoardCardAvatarField
        field={field}
        value={person ? (person.name ?? person.email ?? "") : "Kick"}
    >
        {person ? (
            <UserAvatar user={person} size="sm" />
        ) : (
            <KickAvatar size="sm" />
        )}
    </BoardCardAvatarField>
);

/**
 * The blocked tag: List's blocked icon and how many open tasks block this
 * one, as a button that opens the task's Dependencies. Its tooltip names
 * the only blocker, cut off when long. Tab reaches it from its card.
 */
export const TaskCardBlocked = ({
    task,
    onClick,
}: {
    task: TaskBlockers;
    onClick: () => void;
}) => {
    const tabStopProps = useBoardCardTabStop();
    const label = getBlockedByLabel(task);
    return (
        <Tooltip content={<span className={styles.tooltipLine}>{label}</span>}>
            <button
                {...tabStopProps}
                type="button"
                aria-label={label}
                className={getTaskChipClassName("chip")}
                onClick={onClick}
            >
                <BoardCardTag
                    field={BLOCKED_BY_FIELD}
                    tooltip="none"
                    icon={<TaskBlockedIcon />}
                >
                    {task.numberOfActiveBlockedByTasks}
                </BoardCardTag>
            </button>
        </Tooltip>
    );
};

const COMMENTS_FIELD = "Comments";

const getUnreadLabel = (count: number) =>
    pluralizeWithCount("unread message", count);

/** Unread chat messages read only, as compact cards show them: the chat icon and the count. */
export const TaskCardComments = ({ count }: { count: number }) => (
    <BoardCardTag
        field={COMMENTS_FIELD}
        value={getUnreadLabel(count)}
        tooltip="fieldAndValue"
        icon={<ChatIcon />}
    >
        {count}
    </BoardCardTag>
);

/**
 * Unread chat messages as a tag that opens the task's chat, out of the tab
 * order like the other tags.
 */
export const TaskCardOpenChat = ({
    count,
    onClick,
}: {
    count: number;
    onClick: () => void;
}) => (
    <Tooltip content={OPEN_CHAT_LABEL}>
        <TaskChipButton
            aria-label={`${OPEN_CHAT_LABEL}, ${getUnreadLabel(count)}`}
            onClick={onClick}
        >
            <BoardCardTag
                field={COMMENTS_FIELD}
                tooltip="none"
                icon={<ChatIcon />}
            >
                {count}
            </BoardCardTag>
        </TaskChipButton>
    </Tooltip>
);
